import { PlainDate, PlainTime } from 'temporal-luxon';
import { DCR_EXECUTABLE_TYPES, DCR_MINIMUM_CONNECTOR_VERSION, DCR_NON_EDITABLE_STATUSES, DCR_STATUS_LABELS, DCR_TERMINAL_STATUSES, DCR_TYPE_LABELS, getDcrTypeConfig, } from '../constants';
import { getBranchingStepChanges, hasBranchingChanges } from './data_change_request_branching.utils';
import { EDcrStatus, EDcrType, } from '../types';
export const getDcrTypeLabel = (type) => (type && DCR_TYPE_LABELS[type]) || type || '';
/** Reader-facing status label: stored values stay as-is, so "Open" reads as "New" */
export const getDcrStatusLabel = (status) => (status && DCR_STATUS_LABELS[status]) || status || '';
export const isDcrTerminal = (status) => DCR_TERMINAL_STATUSES.includes(status);
export const canEditDcr = (status) => !DCR_NON_EDITABLE_STATUSES.includes(status);
export const canCancelDcr = (status) => !isDcrTerminal(status);
/**
 * Numeric semver compare, ignoring pre-release tags: release candidates of the minimum
 * (1.11.4-rc.1) ship this experience while it is being qualified, so they count as meeting it.
 * A missing version means the connector predates the version config and never qualifies.
 */
export const meetsMinimumConnectorVersion = (version, minimum = DCR_MINIMUM_CONNECTOR_VERSION) => {
    if (!version) {
        return false;
    }
    const parse = (value) => value
        .split('-')[0]
        .split('.')
        .map((segment) => parseInt(segment, 10) || 0);
    const current = parse(version);
    const required = parse(minimum);
    for (let index = 0; index < Math.max(current.length, required.length); index += 1) {
        const currentSegment = current[index] ?? 0;
        const requiredSegment = required[index] ?? 0;
        if (currentSegment !== requiredSegment) {
            return currentSegment > requiredSegment;
        }
    }
    return true;
};
/** A request only moves on while someone can still act on it */
export const canChangeDcrStatus = (status) => !isDcrTerminal(status);
/**
 * A change is carried out from the one status that says someone is working on it, and only for
 * the types the connector knows how to make.
 */
export const canExecuteDcr = (record) => record?.dcr_intake__status === EDcrStatus.IN_PROGRESS &&
    DCR_EXECUTABLE_TYPES.includes(record?.dcr_intake__type);
/** Why the execute action is unavailable, for the tooltip on the disabled button */
export const getDcrExecuteBlockedReason = (record) => {
    if (!record || canExecuteDcr(record)) {
        return undefined;
    }
    if (!DCR_EXECUTABLE_TYPES.includes(record.dcr_intake__type)) {
        return 'This type of change has to be made by hand and cannot be executed from here.';
    }
    return 'A request can only be executed while it is in progress.';
};
/** Maps a status onto a nucleus Badge variant */
export const getDcrStatusBadgeVariant = (status) => {
    switch (status) {
        case EDcrStatus.OPEN:
            return 'active';
        case EDcrStatus.IN_PROGRESS:
            return 'warning';
        case EDcrStatus.RESOLVED:
            return 'success';
        case EDcrStatus.DECLINED:
        case EDcrStatus.CANCELLED:
            return 'danger';
        default:
            return 'common';
    }
};
export const isEmptyDcrValue = (value) => value === undefined ||
    value === null ||
    value === '' ||
    value === false ||
    (Array.isArray(value) && value.length === 0);
const emptyValueForField = (field) => {
    if (field.type === 'checkbox') {
        return false;
    }
    if (field.type === 'checkbox_list') {
        return [];
    }
    return '';
};
/** A field is only editable once every field it depends on has a value */
export const isDcrFieldEnabled = (field, values) => (field.dependsOn ?? []).every((dependency) => !isEmptyDcrValue(values[dependency]));
/**
 * Derived fields never render. Read-only echoes of upstream data (original value, current status)
 * only appear once the selection they mirror has been made.
 */
export const isDcrFieldVisible = (field, values) => {
    if (field.derived) {
        return false;
    }
    if (field.type === 'readonly') {
        return isDcrFieldEnabled(field, values);
    }
    return true;
};
/** The screen's study configuration decides what the "New value" input looks like */
export const resolveDcrFieldType = (field, screenType) => {
    if (field.dynamicTypeFrom !== 'screen' || !screenType) {
        return field.type;
    }
    switch (screenType) {
        case 'number':
        case 'numeric':
        case 'integer':
        case 'decimal':
            return 'number';
        case 'date':
            return 'date';
        case 'datetime':
        case 'date-time':
            return 'datetime';
        case 'boolean':
        case 'bool':
        case 'yesno':
        case 'choice':
        case 'single-choice':
        case 'text-choice':
        case 'scale':
        case 'vrs':
            return 'select';
        default:
            return 'text';
    }
};
/**
 * Clears downstream values when an upstream select changes; stops a cascade from submitting a
 * screen that belongs to a task response the user has since replaced.
 */
export const clearDcrDependentValues = (changedField, fields, values) => {
    const next = { ...values };
    let cleared = [changedField];
    // Repeat until nothing depends on a field we just cleared
    while (cleared.length) {
        const justCleared = cleared;
        cleared = [];
        for (const field of fields) {
            if ((field.dependsOn ?? []).some((dependency) => justCleared.includes(dependency))) {
                if (!isEmptyDcrValue(next[field.name])) {
                    cleared.push(field.name);
                }
                next[field.name] = emptyValueForField(field);
            }
        }
    }
    return next;
};
/**
 * Equality over values that can be a single answer or a multi-select list: order never decides
 * whether two selections are the same.
 */
const sameDcrValue = (first, second) => {
    const normalize = (value) => Array.isArray(value) ? value.map(String).sort().join(',') : String(value ?? '');
    return normalize(first) === normalize(second);
};
export const validateDcrFields = (fields, values, options, codeValues) => {
    const errors = [];
    for (const field of fields) {
        if (field.type === 'info' || field.derived || field.type === 'readonly') {
            continue;
        }
        if (!isDcrFieldEnabled(field, values)) {
            continue;
        }
        const value = values[field.name];
        // A checkbox states a fact about the change, and leaving it clear states it just as well
        if (field.type === 'checkbox') {
            continue;
        }
        if (field.required && isEmptyDcrValue(value)) {
            errors.push({ name: field.name, message: `${field.label} is required` });
            continue;
        }
        if (isEmptyDcrValue(value)) {
            continue;
        }
        if (field.mustDifferFrom) {
            // A choice screen compares the recorded codes, not the display labels: the original label
            // follows the participant's locale, so a translation-only difference is not a change
            const candidate = codeValues?.[field.name] ?? value;
            const original = codeValues?.[field.mustDifferFrom] ?? values[field.mustDifferFrom] ?? '';
            if (sameDcrValue(candidate, original)) {
                errors.push({ name: field.name, message: `${field.label} must be different from the original value` });
                continue;
            }
        }
        if (field.validateAgainst) {
            const allowed = options?.[field.validateAgainst] ?? [];
            if (allowed.length && !allowed.some((option) => option.value === String(value))) {
                errors.push({ name: field.name, message: 'No site found with this number in this study' });
            }
        }
    }
    return errors;
};
/**
 * Advisory check against the study's participant id format spec (`{SITE}-###`, `P#####`, …),
 * where `#` is a digit and `@` a letter. Unknown tokens are treated as wildcards so a spec we
 * can't fully resolve never produces a false warning.
 */
export const matchesDcrParticipantIdFormat = (value, formatSpec, tokens = {}) => {
    if (!formatSpec || !value) {
        return true;
    }
    const escapeRegex = (input) => input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const segments = formatSpec.match(/({SITE}|{PROTOCOL}|{COUNTRY}|[a-zA-Z0-9]+|[@#]+|[._/-]+)/g) ?? [];
    const pattern = segments
        .map((segment) => {
        if (segment === '{SITE}' || segment === '{PROTOCOL}' || segment === '{COUNTRY}') {
            const token = tokens[segment.slice(1, -1)];
            return token ? escapeRegex(token) : '.+';
        }
        if (/^[@#]+$/.test(segment)) {
            return segment
                .split('')
                .map((character) => (character === '#' ? '\\d' : '[A-Za-z]'))
                .join('');
        }
        return escapeRegex(segment);
    })
        .join('');
    return new RegExp(`^${pattern}$`).test(value);
};
/** Matches free text against the keyword lists so we can nudge users to a structured option */
export const findKeywordSuggestion = (text, keywords) => {
    const normalized = text.trim().toLowerCase();
    if (normalized.length < 4) {
        return undefined;
    }
    let best;
    for (const [key, terms] of Object.entries(keywords)) {
        for (const term of terms) {
            if (normalized.includes(term) && (!best || term.length > best.score)) {
                best = { key, score: term.length };
            }
        }
    }
    return best?.key;
};
export const formatDcrValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
        return value.map((item) => formatDcrValue(item)).join(', ');
    }
    return String(value);
};
/**
 * Date-only answers ("2026-09-17") share one rendering with the original value,
 * which can arrive as a bare date or a full timestamp, so the review diff reads
 * the old and new halves in the same format.
 */
export const formatDcrDate = (value) => {
    const text = String(value ?? '').trim();
    if (!text) {
        return '';
    }
    try {
        return PlainDate.from(text.slice(0, 10)).toLocaleString(undefined, { dateStyle: 'medium' });
    }
    catch {
        return text;
    }
};
/**
 * Date/time answers are stored as wallclock strings ("2026-09-17T14:30") with no timezone,
 * so render them with plain calendar types instead of Date to avoid shifting them.
 */
export const formatDcrWallclock = (value) => {
    const text = String(value ?? '').trim();
    if (!text) {
        return '';
    }
    try {
        const [datePart, timePart] = text.split('T');
        const date = PlainDate.from(datePart).toLocaleString(undefined, { dateStyle: 'medium' });
        const time = timePart ? PlainTime.from(timePart).toLocaleString(undefined, { timeStyle: 'short' }) : '';
        return time ? `${date}, ${time}` : date;
    }
    catch {
        return text;
    }
};
/**
 * Calendar day of a response instant. The instant is UTC; the day is read in the
 * participant's zone, then the zone stored on the response, then UTC.
 */
export const formatDcrResponseDate = (value, timeZones) => {
    const date = value ? new Date(value) : undefined;
    if (!date || Number.isNaN(date.getTime())) {
        return '';
    }
    for (const zone of [...timeZones, 'UTC']) {
        if (!zone) {
            continue;
        }
        try {
            return date.toLocaleDateString(undefined, { timeZone: zone });
        }
        catch {
            // An unrecognised zone is skipped. UTC is last and always valid.
        }
    }
    return '';
};
/** The response as the site refers to it: number, activity, and the day it ended */
export const dcrResponseOptionLabel = (taskResponse, participantTimeZone) => [
    taskResponse.c_number,
    taskResponse.c_task?.c_name,
    formatDcrResponseDate(taskResponse.c_end, [participantTimeZone, taskResponse.c_tz]),
]
    .filter(Boolean)
    .join(' — ');
export const findOptionLabel = (options, value) => {
    if (isEmptyDcrValue(value)) {
        return '';
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => findOptionLabel(options, item))
            .filter(Boolean)
            .join(', ');
    }
    return options?.find((option) => option.value === String(value))?.label ?? String(value);
};
/**
 * Ids are stored alongside the human-readable name captured at submission time, so a request
 * still reads correctly later even when the underlying visit or activity has been renamed.
 */
const CHANGE_LABEL_COMPANIONS = {
    dcr_intake__visit_id: ['dcr_intake__visit_name'],
    dcr_intake__desired_visit_id: ['dcr_intake__desired_visit_name'],
    dcr_intake__activity_id: ['dcr_intake__task_name'],
    dcr_intake__task_response_id: ['dcr_intake__task_response_number'],
    // The question reads as the site asked it; the screen's code is all a request raised before it has
    dcr_intake__step_response_id: ['dcr_intake__step_question', 'dcr_intake__step_name'],
};
/** Renders a stored change as text: the captured name first, then an option label, then the raw value */
export const resolveDcrDisplayValue = (field, changes, options) => {
    const captured = (CHANGE_LABEL_COMPANIONS[field.name] ?? [])
        .map((companion) => changes[companion])
        .find((value) => !isEmptyDcrValue(value));
    if (!isEmptyDcrValue(captured)) {
        return formatDcrValue(captured);
    }
    const value = changes[field.name];
    if (field.optionSource && options?.[field.optionSource]) {
        const label = findOptionLabel(options[field.optionSource], value);
        if (label) {
            return label;
        }
    }
    if (field.type === 'date') {
        return formatDcrDate(value);
    }
    if (field.type === 'datetime') {
        return formatDcrWallclock(value);
    }
    return formatDcrValue(value);
};
/**
 * Builds the review/detail diff table. Fields tagged old/new pair up by their diff label, so a
 * type can contribute more than one row. Moving responses is the exception: each moved activity
 * gets its own row, because the change being described is per activity.
 */
export const buildDcrDiffRows = (type, changes, resolveValue, activityLabels) => {
    const config = getDcrTypeConfig(type);
    if (!config) {
        return [];
    }
    // A branching activity replaces the single old/new pair with one row per screen it visits
    if (hasBranchingChanges(changes)) {
        return getBranchingStepChanges(changes).map((step) => ({
            label: step.screen,
            oldValue: step.originalValue,
            newValue: step.selectedValue,
        }));
    }
    const resolve = (field) => (resolveValue ?? ((_, value) => formatDcrValue(value)))(field, changes[field.name]);
    const fieldByName = (name) => config.fields.find((field) => field.name === name);
    if (config.type === EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT) {
        const originalVisit = fieldByName('dcr_intake__visit_id');
        const desiredVisit = fieldByName('dcr_intake__desired_visit_id');
        const activities = Array.isArray(changes.dcr_intake__activities_to_move)
            ? changes.dcr_intake__activities_to_move
            : [];
        return activities.map((activity) => ({
            label: findOptionLabel(activityLabels, activity) || 'Activity',
            oldValue: originalVisit ? resolve(originalVisit) : '',
            newValue: desiredVisit ? resolve(desiredVisit) : '',
        }));
    }
    const rows = new Map();
    for (const field of config.fields) {
        if (!field.diffRole) {
            continue;
        }
        const label = field.diffLabel ?? field.label;
        const row = rows.get(label) ?? { label, oldValue: '', newValue: '' };
        if (field.diffRole === 'old') {
            row.oldValue = resolve(field);
        }
        else {
            row.newValue = resolve(field);
        }
        rows.set(label, row);
    }
    return Array.from(rows.values()).filter((row) => row.oldValue !== '' || row.newValue !== '');
};
/** Turns stored changes back into form values for the edit flow */
export const buildDcrFormValues = (record) => {
    const config = getDcrTypeConfig(record.dcr_intake__type);
    const values = {};
    for (const field of config?.fields ?? []) {
        const stored = record.dcr_intake__changes?.[field.name];
        if (field.type === 'checkbox') {
            values[field.name] = stored === true || stored === 'true';
        }
        else if (field.type === 'checkbox_list') {
            values[field.name] = Array.isArray(stored) ? stored : [];
        }
        else {
            values[field.name] = stored ?? '';
        }
    }
    return values;
};
/** Drops empty values so a stored change set only carries what the user actually entered */
export const buildDcrChangesPayload = (fields, values) => {
    const changes = {};
    for (const field of fields) {
        if (field.type === 'info') {
            continue;
        }
        const value = values[field.name];
        if (isEmptyDcrValue(value) && field.type !== 'checkbox') {
            continue;
        }
        changes[field.name] = value;
    }
    return changes;
};
